/**
 * PortfolioRiskIntelligence - Phase 5 Part B
 * 
 * Executive-level risk and exposure intelligence.
 * Categorized risk clusters, NOT composite scores.
 * 
 * This is strategic oversight, not panic management.
 * Professional executive presentation - no red flashing alerts.
 */
import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import {
  TrendingUp,
  AlertTriangle,
  DollarSign,
  Activity,
  Clock,
  BarChart3,
  Shield,
  ChevronDown,
  ChevronUp,
  Loader2,
  RefreshCw,
  CheckCircle2,
  XCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortfolioRiskIntelligence({ token }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({
    financial: false,
    statistical: false,
    roi: false,
    stalled: false,
    sustainability: false,
  });

  const fetchIntelligence = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/dashboard/portfolio/intelligence`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error("Failed to fetch portfolio intelligence:", error);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (token) {
      fetchIntelligence();
    }
  }, [token]);

  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const toggleSection = (section) => {
    setExpanded({ ...expanded, [section]: !expanded[section] });
  };

  if (loading && !data) {
    return (
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg p-8">
        <div className="flex items-center justify-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          <span className="text-muted-foreground">Loading portfolio intelligence...</span>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg p-8">
        <div className="text-center">
          <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
          <p className="text-muted-foreground mb-4">Portfolio risk intelligence unavailable</p>
          <Button onClick={fetchIntelligence} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const { summary_metrics: summary } = data;
  const hasRisks = summary.high_risk_count > 0;

  return (
    <div className="space-y-6" data-testid="portfolio-risk-intelligence">
      {/* Executive Snapshot */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-500/10 rounded-lg">
              <Shield className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <h3 className="font-semibold text-[hsl(var(--foreground))]">
                Portfolio Risk Intelligence
              </h3>
              <p className="text-sm text-muted-foreground">
                Executive oversight · Last updated: {new Date(data.evaluated_at).toLocaleTimeString()}
              </p>
            </div>
          </div>
          <Button
            onClick={fetchIntelligence}
            disabled={loading}
            variant="outline"
            size="sm"
            data-testid="refresh-portfolio-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-[hsl(var(--background))] rounded-lg p-4">
            <div className="text-sm text-muted-foreground mb-1">Projects at Risk</div>
            <div className={`text-2xl font-bold ${hasRisks ? 'text-amber-400' : 'text-emerald-400'}`}>
              {summary.projects_needing_attention}
            </div>
            <div className="text-xs text-muted-foreground">
              of {summary.active_projects} active
            </div>
          </div>

          <div className="bg-[hsl(var(--background))] rounded-lg p-4">
            <div className="text-sm text-muted-foreground mb-1">Exposure at Risk</div>
            <div className={`text-2xl font-bold ${summary.high_exposure_total > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
              {formatCurrency(summary.high_exposure_total)}
            </div>
            <div className="text-xs text-muted-foreground">
              of {formatCurrency(summary.total_estimated_savings)} total
            </div>
          </div>

          <div className="bg-[hsl(var(--background))] rounded-lg p-4">
            <div className="text-sm text-muted-foreground mb-1">Statistically Strong</div>
            <div className={`text-2xl font-bold ${summary.statistically_strong_pct >= 80 ? 'text-emerald-400' : summary.statistically_strong_pct >= 60 ? 'text-amber-400' : 'text-red-400'}`}>
              {summary.statistically_strong_pct}%
            </div>
            <div className="text-xs text-muted-foreground">
              of analyzed projects
            </div>
          </div>

          <div className="bg-[hsl(var(--background))] rounded-lg p-4">
            <div className="text-sm text-muted-foreground mb-1">On Track</div>
            <div className="text-2xl font-bold text-emerald-400">
              {summary.projects_on_track}
            </div>
            <div className="text-xs text-muted-foreground">
              projects
            </div>
          </div>
        </div>

        {/* Status Summary */}
        {!hasRisks ? (
          <div className="flex items-center gap-2 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            <span className="text-sm text-emerald-400">
              Portfolio health is stable. No critical risks requiring immediate attention.
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <span className="text-sm text-amber-400">
              {summary.high_risk_count} project(s) require executive attention. Review categories below.
            </span>
          </div>
        )}
      </div>

      {/* Risk Categories */}
      <div className="space-y-3">
        {/* High Financial Exposure */}
        {data.high_financial_exposure.length > 0 && (
          <RiskCategory
            title="High Financial Exposure"
            subtitle="Projects with significant savings but uncertain methodology"
            count={data.high_financial_exposure.length}
            icon={DollarSign}
            iconColor="text-amber-400"
            bgColor="bg-amber-500/10"
            borderColor="border-amber-500/20"
            expanded={expanded.financial}
            onToggle={() => toggleSection("financial")}
          >
            {data.high_financial_exposure.map((item, i) => (
              <div key={i} className="bg-[hsl(var(--background))] rounded-lg p-3 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[hsl(var(--foreground))]">{item.project_name}</span>
                  <span className="text-amber-400 font-semibold">{formatCurrency(item.estimated_savings)}</span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-muted-foreground">Phase: {item.phase}</span>
                  <span className={`px-2 py-0.5 rounded ${item.confidence_level === 'Low' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                    {item.confidence_level} Confidence
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Uncertainty: {item.primary_uncertainty}
                </div>
              </div>
            ))}
          </RiskCategory>
        )}

        {/* Statistical Risk */}
        {data.statistical_risk_projects.length > 0 && (
          <RiskCategory
            title="Statistical Risk"
            subtitle="Projects with methodology or sample concerns"
            count={data.statistical_risk_projects.length}
            icon={BarChart3}
            iconColor="text-red-400"
            bgColor="bg-red-500/10"
            borderColor="border-red-500/20"
            expanded={expanded.statistical}
            onToggle={() => toggleSection("statistical")}
          >
            {data.statistical_risk_projects.map((item, i) => (
              <div key={i} className="bg-[hsl(var(--background))] rounded-lg p-3 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[hsl(var(--foreground))]">{item.project_name}</span>
                  <span className="text-xs text-muted-foreground capitalize">{item.phase}</span>
                </div>
                <div className="text-sm text-red-400">
                  {item.specific_issue}
                </div>
                <span className="inline-block text-xs px-2 py-0.5 rounded bg-red-500/10 text-red-400 capitalize">
                  {item.risk_type.replace(/_/g, " ")}
                </span>
              </div>
            ))}
          </RiskCategory>
        )}

        {/* Sustainability Risk */}
        {data.sustainability_risk_projects.length > 0 && (
          <RiskCategory
            title="Sustainability Risk"
            subtitle="Control phase projects with drift or sustainability concerns"
            count={data.sustainability_risk_projects.length}
            icon={Activity}
            iconColor="text-cyan-400"
            bgColor="bg-cyan-500/10"
            borderColor="border-cyan-500/20"
            expanded={expanded.sustainability}
            onToggle={() => toggleSection("sustainability")}
          >
            {data.sustainability_risk_projects.map((item, i) => (
              <div key={i} className="bg-[hsl(var(--background))] rounded-lg p-3 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[hsl(var(--foreground))]">{item.project_name}</span>
                  <span className={`text-sm font-semibold ${item.sustainability_score >= 75 ? 'text-amber-400' : 'text-red-400'}`}>
                    Score: {item.sustainability_score}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className={`px-2 py-0.5 rounded ${
                    item.drift_status === 'Out of Control' ? 'bg-red-500/20 text-red-400' :
                    item.drift_status === 'Early Warning' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-slate-500/20 text-slate-400'
                  }`}>
                    {item.drift_status}
                  </span>
                  {item.kpi_impacted && (
                    <span className="text-muted-foreground">
                      KPI: {item.kpi_impacted} ({item.drift_type})
                    </span>
                  )}
                </div>
              </div>
            ))}
          </RiskCategory>
        )}

        {/* Stalled Projects */}
        {data.stalled_projects.length > 0 && (
          <RiskCategory
            title="Stalled Projects"
            subtitle="Projects with no recent activity"
            count={data.stalled_projects.length}
            icon={Clock}
            iconColor="text-slate-400"
            bgColor="bg-slate-500/10"
            borderColor="border-slate-500/20"
            expanded={expanded.stalled}
            onToggle={() => toggleSection("stalled")}
          >
            {data.stalled_projects.map((item, i) => (
              <div key={i} className="bg-[hsl(var(--background))] rounded-lg p-3 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[hsl(var(--foreground))]">{item.project_name}</span>
                  <span className="text-sm text-slate-400">{item.days_inactive} days inactive</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Phase: {item.current_phase} · Last activity: {item.last_activity}
                </div>
              </div>
            ))}
          </RiskCategory>
        )}

        {/* Weak ROI */}
        {data.weak_roi_projects.length > 0 && (
          <RiskCategory
            title="Weak ROI Logic"
            subtitle="Projects with unsupported optimistic returns"
            count={data.weak_roi_projects.length}
            icon={TrendingUp}
            iconColor="text-purple-400"
            bgColor="bg-purple-500/10"
            borderColor="border-purple-500/20"
            expanded={expanded.roi}
            onToggle={() => toggleSection("roi")}
          >
            {data.weak_roi_projects.map((item, i) => (
              <div key={i} className="bg-[hsl(var(--background))] rounded-lg p-3 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[hsl(var(--foreground))]">{item.project_name}</span>
                  <span className="text-purple-400 font-semibold">{item.claimed_roi}% ROI</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  {item.issue}
                </div>
              </div>
            ))}
          </RiskCategory>
        )}

        {/* No Risks Message */}
        {!hasRisks && (
          <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <h4 className="font-medium text-[hsl(var(--foreground))] mb-1">No Active Risks</h4>
            <p className="text-sm text-muted-foreground">
              All projects are within acceptable risk parameters.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Reusable Risk Category Component
function RiskCategory({ 
  title, 
  subtitle, 
  count, 
  icon: Icon, 
  iconColor, 
  bgColor,
  borderColor,
  expanded, 
  onToggle, 
  children 
}) {
  return (
    <div className={`bg-[hsl(var(--card))] border ${borderColor} rounded-lg overflow-hidden`}>
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between text-left hover:bg-[hsl(var(--background))] transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 ${bgColor} rounded-lg`}>
            <Icon className={`w-4 h-4 ${iconColor}`} />
          </div>
          <div>
            <div className="font-medium text-[hsl(var(--foreground))] flex items-center gap-2">
              {title}
              <span className={`text-xs px-2 py-0.5 rounded-full ${bgColor} ${iconColor}`}>
                {count}
              </span>
            </div>
            <div className="text-xs text-muted-foreground">{subtitle}</div>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
}
